'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CCSD_SEARCH_FLAG_KEY,
  getCcsdSearchRegistryDefault,
} from '@/features/search';
import { isRuntimeFlagEnabled } from '@/lib/runtimeFlagsClient';

/**
 * Whether the consultants-only CCSD/BUPA code search UX is enabled.
 * Registry default first (synchronous, no flash of the consultants-only
 * view), then reconciled with the runtime flag map served by
 * `/api/feature-flags` — so a dashboard toggle applies without a redeploy.
 */
export function useCcsdSearchEnabled(): boolean {
  const registryDefault = useMemo(() => getCcsdSearchRegistryDefault(), []);
  const [enabled, setEnabled] = useState(registryDefault);

  useEffect(() => {
    let active = true;
    isRuntimeFlagEnabled(CCSD_SEARCH_FLAG_KEY, registryDefault).then(
      (value) => {
        if (active) setEnabled(value);
      },
    );
    return () => {
      active = false;
    };
  }, [registryDefault]);

  return enabled;
}

/** Tracks the mobile breakpoint used to switch the speciality filter UI. */
export function useIsMobile(breakpoint = '(max-width: 639px)'): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(breakpoint);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [breakpoint]);

  return isMobile;
}

interface DismissableRefs {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
}

/** Closes open dropdowns on outside click. */
export function useDismissOnOutsideClick({
  containerRef,
  onClose,
}: DismissableRefs): void {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    // Keep the latest callback available to the listener without re-binding.
    onCloseRef.current = onClose;

    const handleOutsideClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onCloseRef.current();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [containerRef, onClose]);
}

interface KeyboardNavOptions {
  suggestionCount: number;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  onSelectSuggestion: (index: number) => void;
  onSubmitSearch: () => void;
  onClose: () => void;
  enabled: boolean;
}

/**
 * Arrow-key / Enter / Escape handling for the autocomplete listbox.
 * Scrolls the active option into view on keyboard navigation only.
 */
export function useAutocompleteKeyboardNav({
  suggestionCount,
  dropdownRef,
  onSelectSuggestion,
  onSubmitSearch,
  onClose,
  enabled,
}: KeyboardNavOptions) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const isKeyboardNavRef = useRef(false);

  const resetActiveIndex = useCallback(() => {
    isKeyboardNavRef.current = false;
    setActiveIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!enabled || suggestionCount === 0) {
        if (e.key === 'Enter') onSubmitSearch();
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          isKeyboardNavRef.current = true;
          setActiveIndex((prev) => (prev < suggestionCount - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          isKeyboardNavRef.current = true;
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestionCount - 1));
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'Enter':
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < suggestionCount) {
            onSelectSuggestion(activeIndex);
          } else {
            onSubmitSearch();
          }
          break;
      }
    },
    [
      enabled,
      suggestionCount,
      activeIndex,
      onSelectSuggestion,
      onSubmitSearch,
      onClose,
    ],
  );

  useEffect(() => {
    if (!isKeyboardNavRef.current || activeIndex < 0 || !dropdownRef.current)
      return;
    const items =
      dropdownRef.current.querySelectorAll<HTMLElement>('[role="option"]');
    items[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, dropdownRef]);

  return { activeIndex, setActiveIndex, resetActiveIndex, handleKeyDown };
}
